/**
 * Dominio de Proyectos + Tablero Kanban (Fase 1).
 *
 * Tipos alineados 1:1 al contrato de la API y hooks de TanStack Query.
 * Toda escritura filtra por empresa en el backend (multi-tenant); acá sólo
 * consumimos el contrato. La cache del tablero se maneja bajo la key
 * ['board', projectId] y se actualiza de forma optimista para que el drag &
 * drop se sienta instantáneo (ver BoardPage para el flujo de reordenamiento).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

// --- Tipos de dominio -------------------------------------------------------

export type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

/**
 * Visibilidad del proyecto (control de acceso, enforzado en el backend):
 *   - PUBLIC  : todos los miembros de la empresa ven todas las tareas.
 *   - PRIVATE : un MEMBER no privilegiado sólo ve tareas donde está asignado
 *     (a la tarea o a una subtarea); el admin y el creador ven todas.
 * El front sólo refleja lo que el backend devuelve (no filtra tareas).
 */
export type ProjectVisibility = 'PUBLIC' | 'PRIVATE';

export interface Project {
  id: string;
  name: string;
  description: string | null;
  status: string;
  /** Visibilidad del proyecto (default PUBLIC). Alimenta el badge "Privado". */
  visibility: ProjectVisibility;
  created_at: string;
  task_count: number;
  /** Métricas para el card (mini tablero de control). Pueden faltar en respuestas
   *  que no las hidratan (ej. creación); default 0 al renderizar. */
  done_count?: number;
  open_count?: number;
  overdue_count?: number;
  member_count?: number;
  comment_count?: number;
  /** Preferencia POR-USUARIO: el proyecto está fijado arriba (favorito). */
  is_favorite: boolean;
  /** Preferencia POR-USUARIO: orden manual (drag & drop). Grande si no hay pref. */
  position: number;
  /** Carpeta a la que pertenece el proyecto (agrupación); null = sin carpeta (raíz). */
  folder_id: string | null;
}

export interface TemplateColumn {
  name: string;
  color: string;
}

export interface Template {
  id: string;
  name: string;
  description: string | null;
  vertical_id: string;
  columns: TemplateColumn[];
  field_count: number;
}

export interface Task {
  id: string;
  title: string;
  description?: string | null;
  priority: Priority;
  assignee_id: string | null;
  due_date: string | null;
  /** Evento/agenda (feature calendar_agenda): inicio con hora, fin, lugar, link de sala. */
  start_at?: string | null;
  end_at?: string | null;
  location?: string | null;
  meeting_url?: string | null;
  /**
   * "Datos del caso": valores de campos personalizados (mapa key => valor). Las
   * DEFINICIONES viven en `Board.custom_fields`; acá sólo los valores de esta
   * tarea. null/ausente = sin datos.
   */
  custom_values?: Record<string, string | number> | null;
  position: number;
  /** Cantidad de subtareas (viene en el board; puede faltar en respuestas puntuales). */
  subtask_count?: number;
  /** Presente en respuestas de creación/movimiento; lo mantenemos en cache. */
  column_id?: string;
  /**
   * Dependencias (Fase 3, aditivo). El board marca si la tarea está bloqueada
   * por otras (al menos una "bloqueada por" sin completar) y cuántas la
   * bloquean. Puede faltar en respuestas puntuales que no hidratan el grafo.
   */
  is_blocked?: boolean;
  blocked_by_count?: number;
  /**
   * Cantidad de subtareas OBLIGATORIAS sin completar. Viene en el board; si es
   * > 0 la tarea no puede cambiar de columna (el backend rechaza el move con
   * `subtask_required_incomplete`) y la tarjeta muestra el indicador de traba.
   */
  required_open_count?: number;
  /** IDs de los responsables de las SUBTAREAS (viene en el board), para mostrarlos
   *  en la lista aunque la tarea principal no tenga responsable. */
  subtask_assignee_ids?: string[];
  /** Resumen de actividad en la tarjeta (viene en el board). */
  comment_count?: number;
  attachment_count?: number;
  /** Comentarios de OTROS posteriores a mi última lectura (badge "sin leer"). */
  unread_count?: number;
}

/** Subtarea (checklist rico): asignable a un miembro y opcionalmente obligatoria. */
export interface Subtask {
  id: string;
  title: string;
  is_done: boolean;
  /**
   * Marca de obligatoria. Cuando es `true`, la subtarea gatea el pase de la tarea
   * a la columna `required_for_column_id` (el backend rechaza el move con
   * `subtask_required_incomplete` hasta completarla u omitirla).
   */
  is_required: boolean;
  /** Columna que esta obligatoria gatea (uuid de `project_column`); null si no es obligatoria. */
  required_for_column_id: string | null;
  /** Nombre legible de esa columna (ej. "Done"), para mostrar en el chip. */
  required_column_name: string | null;
  /** Omitida con motivo: sigue siendo obligatoria pero ya no bloquea el move. */
  waived: boolean;
  /** Motivo de la omisión (subtítulo/tooltip del chip "Omitida"). */
  waived_reason: string | null;
  assignee_id: string | null;
  /** Fecha de vencimiento (YYYY-MM-DD); nunca posterior a la fecha límite de la tarea. */
  due_date: string | null;
  position: number;
}

/**
 * Columna del proyecto en su forma mínima, para el desplegable "para pasar a…"
 * del editor de subtareas. `Column` la satisface estructuralmente.
 */
export interface SubtaskColumn {
  id: string;
  name: string;
  position: number;
}

/** Una subtarea obligatoria sin completar que traba el move (viene en el error). */
export interface BlockingSubtask {
  id: string;
  title: string;
  required_column_name: string;
}

/** Cuerpo del error `subtask_required_incomplete` que devuelve PATCH /tasks/{id}/move. */
export interface SubtaskRequiredIncomplete {
  code: 'subtask_required_incomplete';
  open: number;
  blocking: BlockingSubtask[];
}

/** Categoría de flujo de una columna. DONE = columna final (cierra la tarea). */
export type ColumnCategory = 'TODO' | 'IN_PROGRESS' | 'DONE';

export interface Column {
  id: string;
  name: string;
  /**
   * Categoría de flujo (migr. 051). 'DONE' marca una columna FINAL: una tarea ahí
   * se considera terminada → no cuenta como vencida, no dispara recordatorios y
   * destraba dependencias. Puede faltar en respuestas viejas (default 'TODO').
   */
  category?: ColumnCategory;
  color: string;
  position: number;
  wip_limit: number | null;
  tasks: Task[];
}

/** Tipo de dato de un campo personalizado ("dato del caso"). */
export type CustomFieldType = 'text' | 'number' | 'select' | 'date' | 'person';

/**
 * Definición de un campo personalizado del proyecto ("datos del caso"). Los
 * VALORES por tarea viven en `Task.custom_values[key]`. `show_in_calendar` marca
 * que ese dato se muestre en el chip del calendario (feature calendar_agenda);
 * se aplica a TODO el proyecto (todas las tareas heredan el flag).
 */
export interface CustomFieldDef {
  id: string;
  key: string;
  label: string;
  type: CustomFieldType;
  /** Opciones para type 'select' (lista de strings o {value,label}); null si no aplica. */
  options: Array<string | { value?: string; label?: string }> | null;
  position: number;
  show_in_calendar: boolean;
}

export interface Member {
  id: string;
  first_name: string;
  last_name: string;
  avatar_path: string | null;
  /**
   * Email del miembro. Necesario para las @menciones en comentarios (el
   * composer inserta `@{email}`). Puede faltar según el endpoint que hidrata
   * al board; los miembros sin email no se ofrecen para mencionar.
   */
  email?: string | null;
}

/**
 * Responsables de las SUBTAREAS de una tarea, resueltos a `Member` y
 * deduplicados contra el owner (para no repetir su avatar). Se usa en todas las
 * vistas (Kanban, Lista, Calendario, Timeline) para mostrar todo el equipo
 * involucrado en la tarjeta/fila, no sólo al responsable principal.
 */
export function subtaskAssigneesOf(
  task: Pick<Task, 'assignee_id' | 'subtask_assignee_ids'>,
  members: Map<string, Member>,
): Member[] {
  const seen = new Set<string>();
  const out: Member[] = [];
  for (const id of task.subtask_assignee_ids ?? []) {
    if (id === task.assignee_id || seen.has(id)) continue;
    const m = members.get(id);
    if (m) {
      seen.add(id);
      out.push(m);
    }
  }
  return out;
}

export interface Board {
  project: {
    id: string;
    name: string;
    description: string | null;
    status: string;
    visibility: ProjectVisibility;
    /**
     * ¿El usuario actual puede ESCRIBIR en este proyecto (crear/editar/mover
     * tareas, columnas, automatizaciones)? true = admin o integrante del equipo
     * del proyecto. false = espectador: sólo puede VER y COMENTAR. Lo resuelve el
     * backend; el front sólo lo refleja (independiente del read-only por trial).
     */
    can_write: boolean;
  };
  columns: Column[];
  members: Member[];
  /** Definiciones de "datos del caso" del proyecto (feature calendar_agenda). Puede faltar. */
  custom_fields?: CustomFieldDef[];
}

/**
 * Integrante del EQUIPO de un proyecto (subconjunto de los miembros de la
 * empresa que pueden escribir en él). Sale de GET /projects/{id}/team; es
 * distinto de `board.members` (todos los miembros de la empresa, para asignar).
 */
export interface ProjectTeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_path: string | null;
  /** Quién lo agregó al equipo (uuid + nombre legible). Puede faltar para el creador. */
  added_by: string | null;
  added_by_name: string | null;
  created_at: string;
}

// --- Keys de cache ----------------------------------------------------------

export const projectsKey = ['projects'] as const;
export const templatesKey = ['templates'] as const;
export const boardKey = (projectId: string) => ['board', projectId] as const;
export const subtasksKey = (taskId: string) => ['subtasks', taskId] as const;
export const projectTeamKey = (projectId: string) => ['project-team', projectId] as const;

// --- Payloads ---------------------------------------------------------------

export interface CreateProjectPayload {
  name: string;
  description?: string;
  template_id?: string;
  visibility?: ProjectVisibility;
  /** Carpeta destino al crear (opcional); null/ausente = sin carpeta. */
  folder_id?: string | null;
}

export interface UpdateProjectPayload {
  name?: string;
  description?: string | null;
  visibility?: ProjectVisibility;
  /** Mover a otra carpeta; null = a la raíz (sin carpeta). */
  folder_id?: string | null;
}

/**
 * "Convertir tarea en proyecto": crea un proyecto y MUEVE la tarea original a él
 * (conserva comentarios/adjuntos/descripción/responsable); sus subtareas se
 * promueven a tareas hermanas.
 */
export interface ConvertToProjectPayload {
  /** Nombre del proyecto; si se omite, usa el título de la tarea. */
  name?: string;
  /** Carpeta destino (opcional); null/ausente = sin carpeta. */
  folder_id?: string | null;
}

export interface ConvertToProjectResult {
  id: string;
  name: string;
  folder_id: string | null;
  /** Cuántas subtareas se promovieron a tareas. */
  migrated_subtasks: number;
}

export interface CreateTaskPayload {
  column_id: string;
  title: string;
  priority?: Priority;
  assignee_id?: string | null;
  due_date?: string | null;
  /** Descripción inicial (el backend la persiste). Usada al importar notas de Plaud. */
  description?: string | null;
}

export interface UpdateTaskPayload {
  title?: string;
  description?: string | null;
  priority?: Priority;
  assignee_id?: string | null;
  due_date?: string | null;
  /** Campos de evento/agenda (feature calendar_agenda). */
  start_at?: string | null;
  end_at?: string | null;
  location?: string | null;
  meeting_url?: string | null;
  /** "Datos del caso": mapa key => valor (null/vacío limpia). Se valida contra las defs. */
  custom_values?: Record<string, string | number> | null;
}

// --- Helpers puros de reordenamiento (usados por el tablero) ----------------

/** Ubica una tarea dentro del set de columnas. */
export function locateTask(
  columns: Column[],
  taskId: string,
): { colIndex: number; taskIndex: number } | null {
  for (let colIndex = 0; colIndex < columns.length; colIndex++) {
    const taskIndex = columns[colIndex].tasks.findIndex((tk) => tk.id === taskId);
    if (taskIndex !== -1) return { colIndex, taskIndex };
  }
  return null;
}

/**
 * Devuelve nuevas columnas con la tarea `taskId` movida a `toColumnId` en el
 * índice `toIndex`. Reindexa `position` = índice en cada columna afectada y
 * setea `column_id` en la tarea movida. Inmutable (nuevas referencias).
 */
export function applyMove(
  columns: Column[],
  taskId: string,
  toColumnId: string,
  toIndex: number,
): Column[] {
  const from = locateTask(columns, taskId);
  if (!from) return columns;

  const moving = columns[from.colIndex].tasks[from.taskIndex];
  const next = columns.map((col) => ({ ...col, tasks: [...col.tasks] }));

  // Sacar de origen.
  next[from.colIndex].tasks.splice(from.taskIndex, 1);

  // Insertar en destino.
  const destIndex = next.findIndex((c) => c.id === toColumnId);
  if (destIndex === -1) return columns;
  const clampedIndex = Math.max(0, Math.min(toIndex, next[destIndex].tasks.length));
  next[destIndex].tasks.splice(clampedIndex, 0, { ...moving, column_id: toColumnId });

  // Reindexar posiciones de origen y destino.
  reindex(next[from.colIndex]);
  reindex(next[destIndex]);
  return next;
}

function reindex(col: Column): void {
  col.tasks = col.tasks.map((tk, i) => (tk.position === i ? tk : { ...tk, position: i }));
}

/** Ordena columnas y tareas por `position` (defensivo ante datos del server). */
export function sortBoard(board: Board): Board {
  const byPos = <T extends { position: number }>(a: T, b: T) => a.position - b.position;
  return {
    ...board,
    columns: [...board.columns]
      .sort(byPos)
      .map((col) => ({ ...col, tasks: [...col.tasks].sort(byPos) })),
  };
}

// --- Hooks: proyectos y plantillas ------------------------------------------

export function useProjects() {
  return useQuery({
    queryKey: projectsKey,
    queryFn: () => api.get<Project[]>('/projects'),
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: templatesKey,
    queryFn: () => api.get<Template[]>('/templates'),
    staleTime: 5 * 60_000,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProjectPayload) => api.post<Project>('/projects', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey });
    },
  });
}

/** Renombrar / editar descripción. Body parcial: sólo se envían las claves a tocar. */
export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, patch }: { projectId: string; patch: UpdateProjectPayload }) =>
      api.patch<Project>(`/projects/${projectId}`, patch),
    onSuccess: (_updated, { projectId }) => {
      void qc.invalidateQueries({ queryKey: projectsKey });
      // El board hidrata nombre/descripción del proyecto -> refrescarlo también.
      void qc.invalidateQueries({ queryKey: boardKey(projectId) });
    },
  });
}

/** Borra el proyecto y todas sus tareas (backend en cascada). Sólo COMPANY_ADMIN. */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.delete<unknown>(`/projects/${projectId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey });
    },
  });
}

/**
 * Convierte una tarea en un proyecto nuevo (subtareas → tareas). Invalida la
 * lista de proyectos, las carpetas (cambia el conteo) y el tablero de origen
 * (la tarea pudo eliminarse). `sourceProjectId` = proyecto donde vive la tarea.
 */
export function useConvertTaskToProject(sourceProjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: ConvertToProjectPayload }) =>
      api.post<ConvertToProjectResult>(`/tasks/${taskId}/convert-to-project`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey });
      void qc.invalidateQueries({ queryKey: ['folders'] });
      void qc.invalidateQueries({ queryKey: boardKey(sourceProjectId) });
    },
  });
}

/**
 * Ordena una lista de proyectos igual que el backend (GET /projects):
 * favoritos primero, luego `position` asc, luego `created_at` desc como
 * desempate. Estable e inmutable. Usado por el optimismo de favorito.
 */
export function sortProjects(list: Project[]): Project[] {
  return [...list].sort((a, b) => {
    if (a.is_favorite !== b.is_favorite) return a.is_favorite ? -1 : 1;
    if (a.position !== b.position) return a.position - b.position;
    return b.created_at.localeCompare(a.created_at);
  });
}

/**
 * Toggle de favorito (preferencia por-usuario). Optimista sobre la cache de
 * ['projects']: invierte `is_favorite` del proyecto y re-ordena como el backend
 * (favoritos arriba). Revierte al snapshot si el POST falla.
 */
export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api.post<{ is_favorite: boolean }>(`/projects/${projectId}/favorite`, {}),
    onMutate: async (projectId) => {
      await qc.cancelQueries({ queryKey: projectsKey });
      const prev = qc.getQueryData<Project[]>(projectsKey);
      if (prev) {
        const next = sortProjects(
          prev.map((p) => (p.id === projectId ? { ...p, is_favorite: !p.is_favorite } : p)),
        );
        qc.setQueryData<Project[]>(projectsKey, next);
      }
      return { prev };
    },
    onError: (_err, _projectId, ctx) => {
      if (ctx?.prev) qc.setQueryData(projectsKey, ctx.prev);
    },
  });
}

/**
 * Reordena las tarjetas (preferencia por-usuario). El caller pasa el nuevo orden
 * COMPLETO de ids. Optimista: reordena la cache según `ids` y reindexa
 * `position`; revierte al snapshot si el PATCH falla.
 */
export function useReorderProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.patch<unknown>('/projects/order', { ids }),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: projectsKey });
      const prev = qc.getQueryData<Project[]>(projectsKey);
      if (prev) {
        const byId = new Map(prev.map((p) => [p.id, p]));
        const next = ids
          .map((id, i) => {
            const p = byId.get(id);
            return p ? { ...p, position: i } : undefined;
          })
          .filter((p): p is Project => p !== undefined);
        qc.setQueryData<Project[]>(projectsKey, next);
      }
      return { prev };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData(projectsKey, ctx.prev);
    },
  });
}

// --- Hooks: tablero ---------------------------------------------------------

export function useBoard(projectId: string | undefined) {
  return useQuery({
    queryKey: boardKey(projectId ?? ''),
    queryFn: () => api.get<Board>(`/projects/${projectId}/board`),
    enabled: Boolean(projectId),
    // Refresco en vivo: si un compañero comenta/mueve/crea en otra computadora, lo
    // vemos sin recargar (badge de "sin leer", contadores, tarjetas). Sólo con la
    // pestaña activa (no en background) para no cargar el server de gusto.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Marca los comentarios de una tarea como leídos por el usuario actual. Actualiza
 * la cache del board de forma OPTIMISTA (unread_count -> 0) sin refetch, para que
 * el badge "sin leer" se apague al instante y no se pise el formulario abierto.
 */
export function useMarkTaskRead(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.post<{ ok: boolean }>(`/tasks/${taskId}/read`, {}),
    onSuccess: (_data, taskId) => {
      qc.setQueryData<Board>(boardKey(projectId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          columns: prev.columns.map((col) => ({
            ...col,
            tasks: col.tasks.map((tk) => (tk.id === taskId ? { ...tk, unread_count: 0 } : tk)),
          })),
        };
      });
    },
  });
}

// --- Hooks: equipo del proyecto ---------------------------------------------

/** Integrantes del equipo del proyecto (quiénes pueden escribir en él). */
export function useProjectTeam(projectId: string | undefined) {
  return useQuery({
    queryKey: projectTeamKey(projectId ?? ''),
    queryFn: () => api.get<ProjectTeamMember[]>(`/projects/${projectId}/team`),
    enabled: Boolean(projectId),
  });
}

/**
 * Agrega un miembro de la empresa al equipo del proyecto. Idempotente en el
 * backend (201/200). Invalida el equipo y el board (el `can_write` del recién
 * agregado y las tarjetas dependen de la membresía).
 */
export function useAddProjectMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<unknown>(`/projects/${projectId}/team`, { user_id: userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectTeamKey(projectId) });
      void qc.invalidateQueries({ queryKey: boardKey(projectId) });
    },
  });
}

/** Quita a un integrante del equipo del proyecto (no desasigna sus tareas). */
export function useRemoveProjectMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete<unknown>(`/projects/${projectId}/team/${userId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectTeamKey(projectId) });
      void qc.invalidateQueries({ queryKey: boardKey(projectId) });
    },
  });
}

/**
 * Marca/desmarca un campo personalizado como "mostrar en calendario" (feature
 * calendar_agenda). Optimista sobre la cache del board (los chips del calendario
 * dependen del flag); revierte al snapshot si falla.
 */
export function useUpdateField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, showInCalendar }: { fieldId: string; showInCalendar: boolean }) =>
      api.patch<{ ok: boolean }>(`/projects/${projectId}/fields/${fieldId}`, {
        show_in_calendar: showInCalendar,
      }),
    onMutate: async ({ fieldId, showInCalendar }) => {
      await qc.cancelQueries({ queryKey: boardKey(projectId) });
      const prev = qc.getQueryData<Board>(boardKey(projectId));
      if (prev?.custom_fields) {
        qc.setQueryData<Board>(boardKey(projectId), {
          ...prev,
          custom_fields: prev.custom_fields.map((f) =>
            f.id === fieldId ? { ...f, show_in_calendar: showInCalendar } : f,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(boardKey(projectId), ctx.prev);
    },
  });
}

export function useCreateTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTaskPayload) =>
      api.post<Task>(`/projects/${projectId}/tasks`, payload),
    onSuccess: (task) => {
      // Insertar la tarea creada al final de su columna, sin refetch.
      qc.setQueryData<Board>(boardKey(projectId), (old) => {
        if (!old) return old;
        return {
          ...old,
          columns: old.columns.map((col) =>
            col.id === (task.column_id ?? '')
              ? { ...col, tasks: [...col.tasks, task] }
              : col,
          ),
        };
      });
    },
  });
}

export function useUpdateTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, patch }: { taskId: string; patch: UpdateTaskPayload }) =>
      api.patch<Task>(`/tasks/${taskId}`, patch),
    onSuccess: (updated, { taskId }) => {
      // Fusionar los campos actualizados en la tarea existente (misma columna).
      qc.setQueryData<Board>(boardKey(projectId), (old) => {
        if (!old) return old;
        return {
          ...old,
          columns: old.columns.map((col) => ({
            ...col,
            tasks: col.tasks.map((tk) => (tk.id === taskId ? { ...tk, ...updated } : tk)),
          })),
        };
      });
    },
  });
}

export function useDeleteTask(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api.delete<unknown>(`/tasks/${taskId}`),
    onSuccess: (_res, taskId) => {
      qc.setQueryData<Board>(boardKey(projectId), (old) => {
        if (!old) return old;
        return {
          ...old,
          columns: old.columns.map((col) => ({
            ...col,
            tasks: col.tasks.filter((tk) => tk.id !== taskId),
          })),
        };
      });
    },
  });
}

/**
 * Persiste el movimiento de una tarea. El reordenamiento optimista de la cache
 * lo hace el propio tablero (durante el drag), por eso este hook es delgado:
 * sólo llama al endpoint. El caller pasa `onError` para revertir al snapshot
 * tomado al iniciar el drag.
 */
export function useMoveTask() {
  return useMutation({
    mutationFn: ({
      taskId,
      columnId,
      position,
    }: {
      taskId: string;
      columnId: string;
      position: number;
    }) => api.patch<unknown>(`/tasks/${taskId}/move`, { column_id: columnId, position }),
  });
}

// --- Hooks: subtareas -------------------------------------------------------

export interface CreateSubtaskPayload {
  title: string;
  /** Flag legacy: el backend lo defaultea a la última columna. Preferí `required_for_column_id`. */
  is_required?: boolean;
  /** Columna que gatea la obligatoria (uuid de `project_column`); null la deja opcional. */
  required_for_column_id?: string | null;
  assignee_id?: string | null;
  /** Fecha de vencimiento (YYYY-MM-DD); no puede superar la fecha límite de la tarea. */
  due_date?: string | null;
}

export interface UpdateSubtaskPayload {
  title?: string;
  is_done?: boolean;
  is_required?: boolean;
  /** Setear a un uuid la hace obligatoria para esa columna; null la quita. */
  required_for_column_id?: string | null;
  assignee_id?: string | null;
  /** Fecha de vencimiento (YYYY-MM-DD); null la limpia. No puede superar la de la tarea. */
  due_date?: string | null;
}

export function useSubtasks(taskId: string | undefined) {
  return useQuery({
    queryKey: subtasksKey(taskId ?? ''),
    queryFn: () => api.get<Subtask[]>(`/tasks/${taskId}/subtasks`),
    enabled: Boolean(taskId),
  });
}

/**
 * Invalida la lista de subtareas de la tarea y el board (para refrescar
 * `required_open_count` en las tarjetas). El board se invalida por prefijo
 * ['board', …] igual que hacen las dependencias.
 */
function invalidateSubtasks(qc: ReturnType<typeof useQueryClient>, taskId: string): void {
  void qc.invalidateQueries({ queryKey: subtasksKey(taskId) });
  void qc.invalidateQueries({ queryKey: ['board'] });
}

export function useCreateSubtask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSubtaskPayload) =>
      api.post<Subtask>(`/tasks/${taskId}/subtasks`, payload),
    onSuccess: () => invalidateSubtasks(qc, taskId),
  });
}

export function useUpdateSubtask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subtaskId, patch }: { subtaskId: string; patch: UpdateSubtaskPayload }) =>
      api.patch<Subtask>(`/subtasks/${subtaskId}`, patch),
    onSuccess: () => invalidateSubtasks(qc, taskId),
  });
}

export function useDeleteSubtask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subtaskId: string) => api.delete<unknown>(`/subtasks/${subtaskId}`),
    onSuccess: () => invalidateSubtasks(qc, taskId),
  });
}

/**
 * Omite una subtarea obligatoria con un motivo (obligatorio). La subtarea sigue
 * marcada como obligatoria pero deja de trabar el move. Invalida board+lista.
 */
export function useWaiveSubtask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subtaskId, reason }: { subtaskId: string; reason: string }) =>
      api.post<Subtask>(`/subtasks/${subtaskId}/waive`, { reason }),
    onSuccess: () => invalidateSubtasks(qc, taskId),
  });
}

/** Reactiva (des-omite) una subtarea previamente omitida: vuelve a trabar el move. */
export function useUnwaiveSubtask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subtaskId: string) => api.post<Subtask>(`/subtasks/${subtaskId}/unwaive`, {}),
    onSuccess: () => invalidateSubtasks(qc, taskId),
  });
}
